import { FormEvent, useEffect, useState } from 'react'
import { usersApi } from '@/api/endpoints'
import type { User } from '@/api/types'
import { Button, Card, Field, Input } from '@/components/ui'

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [inviteLink, setInviteLink] = useState<string | null>(null)

  const load = () => usersApi.list().then(setUsers)
  useEffect(() => {
    load()
  }, [])

  const invite = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    const res = await usersApi.createInvite({
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      isAdmin,
    })
    setInviteLink(`${window.location.origin}/invite/${res.inviteToken}`)
    setEmail('')
    setFirstName('')
    setLastName('')
    setIsAdmin(false)
    await load()
  }

  const toggleAdmin = async (u: User) => {
    await usersApi.update(u.id, { isAdmin: !u.isAdmin })
    await load()
  }
  const toggleDisabled = async (u: User) => {
    await usersApi.update(u.id, { status: u.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED' })
    await load()
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Uživatelé</h1>
      <Card>
        <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
          <div className="min-w-52 flex-1">
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
          </div>
          <div className="w-36">
            <Field label="Jméno">
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
          </div>
          <div className="w-36">
            <Field label="Příjmení">
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
            Admin
          </label>
          <Button type="submit">Pozvat</Button>
        </form>
        {inviteLink && (
          <div className="mt-4 rounded-lg bg-brand-50 p-3 text-sm">
            <p className="mb-1 font-medium text-brand-700">Pozvánka vytvořena — pošli tento odkaz:</p>
            <code className="break-all text-brand-800">{inviteLink}</code>
          </div>
        )}
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th className="py-2">Email</th>
                <th className="py-2">Jméno</th>
                <th className="py-2">Stav</th>
                <th className="py-2 text-center">Admin</th>
                <th className="py-2 text-right">Akce</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{u.email}</td>
                  <td className="py-2 text-slate-500">{[u.firstName, u.lastName].filter(Boolean).join(' ')}</td>
                  <td className="py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${
                        u.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : u.status === 'INVITED'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="py-2 text-center">
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => toggleAdmin(u)}>
                      {u.isAdmin ? '✓ admin' : 'nastavit'}
                    </button>
                  </td>
                  <td className="py-2 text-right">
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => toggleDisabled(u)}>
                      {u.status === 'DISABLED' ? 'aktivovat' : 'deaktivovat'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
